import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { useNavigate } from '@solidjs/router'
import { type Component, createMemo, createResource, createSignal, Show } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Chip from '~/components/Chip'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useForm } from '~/hooks/useForm'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import TableFilters, { FiltersButton } from '~/layers/TableFilters'
import { COMPANY_ROLE_OPTIONS, companyRoleColor, companyRoleLabel, userInitials } from '~/services/access.service'
import { permissionService } from '~/services/permission.service'
import { type UserType, userService } from '~/services/user.service'
import { authStore } from '~/stores/auth'
import { resolveError } from '~/utils/error'

import { P } from '~/utils/permissions'

const UsuariosPage: Component = () => {
  const { can, isRoot } = usePermissions()
  const alert = useAlert()
  const toast = useToast()
  const navigate = useNavigate()

  // Búsqueda y filtros server-side
  const [filtersOpen, setFiltersOpen] = createSignal(false)
  const [filterRole, setFilterRole] = createSignal('')
  const [filterCompanyRole, setFilterCompanyRole] = createSignal('')
  const [filterSearch, debouncedSearch, setFilterSearch] = useDebouncedSignal('')
  const [filterStatus, setFilterStatus] = createSignal('')

  const table = useServerTable<UserType>({
    fetcher: params => userService.list(params as Parameters<typeof userService.list>[0]),
    params: () => {
      const q = debouncedSearch().trim()
      const p: Record<string, string | number | undefined> = {}
      if (q) p.name = `%${q}%`
      if (filterStatus()) p.status = filterStatus()
      return p
    }
  })

  const [roles] = createResource(async () => {
    try {
      return await permissionService.listRoles()
    } catch {
      return []
    }
  })

  /** El rol de permisos y el de empresa se filtran en cliente: el listado ya viene completo. */
  const visibleUsers = createMemo(() => {
    const rol = filterRole()
    const empresa = filterCompanyRole()
    return table.data().filter(u => {
      if (rol && String(u.roleId ?? '') !== rol) return false
      if (empresa && u.companyRole !== empresa) return false
      return true
    })
  })

  const activeFilters = createMemo(() => [filterSearch(), filterRole(), filterCompanyRole(), filterStatus()].filter(Boolean).length)

  const clearFilters = () => {
    setFilterSearch('')
    setFilterRole('')
    setFilterCompanyRole('')
    setFilterStatus('')
  }

  const userChips = createMemo(() => [
    ...(filterSearch() ? [{ label: `«${filterSearch()}»`, color: 'info' as const, icon: 'material-symbols-light:search', onRemove: () => setFilterSearch('') }] : []),
    ...(filterRole() ? [{ label: roleName(Number(filterRole())), color: 'primary' as const, icon: 'material-symbols-light:shield-outline', onRemove: () => setFilterRole('') }] : []),
    ...(filterCompanyRole()
      ? [{ label: companyRoleLabel(filterCompanyRole()), color: 'warning' as const, icon: 'material-symbols-light:badge-outline', onRemove: () => setFilterCompanyRole('') }]
      : []),
    ...(filterStatus()
      ? [{ label: filterStatus() === '1' ? 'Activos' : 'Inactivos', color: 'success' as const, icon: 'material-symbols-light:toggle-on-outline', onRemove: () => setFilterStatus('') }]
      : [])
  ])

  const [showModal, setShowModal] = createSignal(false)

  /** El alta pide lo mínimo; el resto (permisos, empresas) se configura en la ficha. */
  const userSchema = z.object({
    name: z.string().min(1, 'El nombre es obligatorio'),
    email: z.string().min(1, 'El correo es obligatorio').email('Correo electrónico inválido'),
    document: z.string().optional(),
    password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    roleId: z.string().optional(),
    companyRole: z.enum(['member', 'admin', 'owner']),
    openDetail: z.boolean().default(true)
  })

  const form = useForm({
    schema: userSchema,
    initialValues: () => ({ name: '', email: '', document: '', password: '', roleId: '', companyRole: 'member', openDetail: true }),
    onSubmit: values => handleCreate(values),
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const roleOptions = () => [{ value: '', title: 'Sin rol' }, ...(roles() ?? []).map(r => ({ value: String(r.id), title: r.name, subtitle: r.description ?? undefined }))]

  const roleName = (id?: number | null) => roles()?.find(r => r.id === id)?.name ?? '-'

  const openCreate = () => {
    form.reset()
    setShowModal(true)
  }

  const handleCreate = async (values: z.infer<typeof userSchema>) => {
    const created = await userService.create({
      email: values.email,
      name: values.name,
      document: values.document || undefined,
      password: values.password,
      ...(values.roleId ? { roleId: Number(values.roleId) } : {}),
      companyRole: values.companyRole
    })
    toast.addToast({ title: 'Usuario creado', type: 'success' })
    setShowModal(false)
    await table.refetch()
    // Lo habitual tras crear es terminar de configurarlo: permisos y empresas.
    if (values.openDetail && created?.id) navigate(`/admin/usuarios/${created.id}`)
  }

  const handleToggleStatus = async (item: UserType) => {
    const activar = item.status !== 1
    try {
      await userService.update(item.id, { status: activar ? 1 : 0 })
      toast.addToast({ title: activar ? 'Usuario activado' : 'Usuario desactivado', type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const handleDelete = async (item: UserType) => {
    const ok = await alert.confirmDelete({
      title: 'Quitar usuario',
      message: `¿Quitar a ${item.name} de esta empresa? Si no pertenece a ninguna otra, se eliminará su cuenta.`
    })
    if (!ok) return
    try {
      await userService.delete(item.id)
      toast.addToast({ title: 'Usuario eliminado', type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Quitar usuarios',
      message: `¿Quitar ${ids.length} ${ids.length === 1 ? 'usuario' : 'usuarios'} de esta empresa? Los que no pertenezcan a ninguna otra se eliminarán sus cuentas. No puedes quitarte a ti mismo.`
    })
    if (!ok) return
    try {
      const res = await userService.bulkDelete(ids)
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'usuario procesado' : 'usuarios procesados'}`, type: 'success' })
      setSelectedIds(new Set<number>())
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const columns = [
    {
      header: 'Usuario',
      sticky: 'left' as const,
      minWidth: 260,
      accessor: (u: UserType) => (
        <div class="flex items-center gap-3">
          <span class="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary">{userInitials(u.name)}</span>
          <div class="min-w-0">
            <div class="flex items-center gap-1.5">
              <span class="truncate font-medium text-foreground">{u.name}</span>
              <Show when={u.id === authStore.user?.id}>
                <Chip size="xs" variant="soft" color="info">
                  tú
                </Chip>
              </Show>
              <Show when={u.isRoot}>
                <Chip size="xs" variant="soft" color="danger" icon="material-symbols-light:shield-person-outline">
                  root
                </Chip>
              </Show>
            </div>
            <span class="block truncate text-xs text-foreground-subtle">{u.email}</span>
          </div>
        </div>
      )
    },
    { header: 'Documento', accessor: (u: UserType) => u.document ?? '-' },
    {
      header: 'Rol de permisos',
      accessor: (u: UserType) => (
        <Show when={u.roleId} fallback={<span class="text-foreground-subtle">Sin rol</span>}>
          <Chip size="sm" variant="soft" color="primary" icon="material-symbols-light:shield-outline">
            {u.roleName ?? roleName(u.roleId)}
          </Chip>
        </Show>
      )
    },
    {
      header: 'Rol en empresa',
      accessor: (u: UserType) =>
        u.companyRole ? (
          <Chip size="sm" variant="soft" color={companyRoleColor(u.companyRole)}>
            {companyRoleLabel(u.companyRole)}
          </Chip>
        ) : (
          <span class="text-foreground-subtle">—</span>
        )
    },
    {
      header: 'Estado',
      accessor: (u: UserType) => (
        <Chip size="sm" variant="soft" color={u.status === 1 ? 'success' : 'default'}>
          {u.status === 1 ? 'Activo' : 'Inactivo'}
        </Chip>
      )
    }
  ]

  return (
    <>
      <Title>Usuarios | Florez Jaramillo</Title>
      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">No se pudieron cargar los usuarios: {resolveError(table.error())}</div>
      </Show>

      <Table<UserType>
        title="Usuarios"
        subtitle="Quién entra al panel, con qué rol y en qué empresas"
        headerActions={
          <div class="flex items-center gap-2">
            <FiltersButton activeCount={activeFilters()} onClick={() => setFiltersOpen(true)} />
            <Show when={can(P.roles.read)}>
              <Button variant="soft" color="info" onClick={() => navigate('/admin/roles')} prependIcon="material-symbols-light:shield-outline">
                Roles
              </Button>
            </Show>
            <Show when={can(P.users.create)}>
              <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
                Nuevo Usuario
              </Button>
            </Show>
          </div>
        }
        data={visibleUsers()}
        columns={columns}
        keyField="id"
        headSticky
        loading={table.loading()}
        serverSide
        selectable={can(P.users.delete)}
        selectableColor="danger"
        onSelectionChange={handleSelectionChange}
        onDeleteSelected={() => void handleBulkDelete()}
        total={table.total()}
        page={table.page()}
        perPage={table.perPage()}
        onPageChange={table.onPageChange}
        onPerPageChange={table.onPerPageChange}
        emptyMessage={isRoot() ? 'Aún no hay usuarios en el sistema' : 'Aún no hay usuarios en esta empresa'}
        actions={item => (
          <ButtonGroup size="sm" variant="soft">
            <Show when={can(P.users.read)}>
              <Button color="info" onClick={() => navigate(`/admin/usuarios/${item.id}`)} tooltip="Configurar" icon="material-symbols-light:settings-outline" />
            </Show>
            <Show when={can(P.users.update) && item.id !== authStore.user?.id}>
              <Button
                color={item.status === 1 ? 'warning' : 'success'}
                onClick={() => handleToggleStatus(item)}
                tooltip={item.status === 1 ? 'Desactivar' : 'Activar'}
                icon={item.status === 1 ? 'material-symbols-light:toggle-off-outline' : 'material-symbols-light:toggle-on-outline'}
              />
            </Show>
            <Show when={can(P.users.delete) && item.id !== authStore.user?.id}>
              <Button color="danger" onClick={() => handleDelete(item)} tooltip="Quitar de la empresa" icon="material-symbols-light:delete-outline" />
            </Show>
          </ButtonGroup>
        )}
      />

      <TableFilters title="Filtros de usuarios" isOpen={filtersOpen()} onClose={() => setFiltersOpen(false)} activeCount={activeFilters()} onClear={clearFilters} chips={userChips()}>
        <div class="space-y-4">
          <Input label="Buscar" value={filterSearch()} onInput={e => setFilterSearch(e.currentTarget.value)} placeholder="Nombre, correo o documento..." startIcon="material-symbols-light:search" />
          <Select label="Rol de permisos" value={filterRole()} options={[{ value: '', title: 'Todos' }, ...roleOptions().slice(1)]} onChange={v => setFilterRole(String(v ?? ''))} />
          <Select label="Rol en la empresa" value={filterCompanyRole()} options={[{ value: '', title: 'Todos' }, ...COMPANY_ROLE_OPTIONS]} onChange={v => setFilterCompanyRole(String(v ?? ''))} />
          <Select
            label="Estado"
            value={filterStatus()}
            options={[
              { value: '', title: 'Todos' },
              { value: '1', title: 'Activos' },
              { value: '0', title: 'Inactivos' }
            ]}
            onChange={v => setFilterStatus(String(v ?? ''))}
          />
        </div>
      </TableFilters>

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">Nuevo Usuario</h2>
        </Modal.Header>
        <Modal.Body>
          <p class="mb-4 text-sm text-foreground-subtle">
            Si el correo ya existe en el sistema, el usuario se vincula a esta empresa en vez de crearse otra cuenta. Los permisos y las empresas se terminan de configurar en su ficha.
          </p>
          <Form id="user-form" controller={form}>
            <div class="space-y-4">
              <Input name="name" label="Nombre" />
              <Input name="email" label="Email" type="email" />
              <Input name="document" label="Documento" />
              <Input name="password" label="Contraseña" type="password" placeholder="Mínimo 8 caracteres" />
              <Select name="roleId" label="Rol de permisos" options={roleOptions()} value={(form.values().roleId as string) ?? ''} />
              <Select name="companyRole" label="Rol en la empresa" options={COMPANY_ROLE_OPTIONS} value={(form.values().companyRole as string) ?? 'member'} />
              <Switch name="openDetail" label="Abrir la ficha al crear" checked={Boolean(form.values().openDetail)} />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={form.submitting() || !form.valid()} form="user-form">
              {form.submitting() ? 'Guardando...' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Show when={isRoot()}>
        <p class="mt-4 text-xs text-foreground-subtle">Como super administrador ves y editas usuarios de cualquier empresa.</p>
      </Show>

      <alert.AlertComponent />
    </>
  )
}

export default UsuariosPage
